const NAME = "Mums & Babies Circle";

export const MUMS_BABIES = {
  en: {
    name: NAME,
    seo: {
      title: "Mums & Babies Postpartum Support Group in English | Rotterdam & Schiedam",
      description: "A six-week postpartum circle in English near Rotterdam for mothers and babies. A space to share your experience, connect with other mothers, and find support in the early months of motherhood.",
    },
    hero: {
      eyebrow: "There is somewhere you can arrive with your baby — and there will also be room for you",
      sub: "Six weeks of space for you and your baby in the early months of motherhood.",
      intro: "A six-week postpartum circle in English for mothers and babies in Schiedam, near Rotterdam — a small weekly group to talk, move, rest and share some of what these first months are really like.",
      facts: ["6 weeks", "2½ hours weekly", "€190", "Mothers + babies", "Small group", "In English", "Schiedam"],
      primaryCta: "Join the next circle",
      secondaryCta: "See dates",
    },
    next: {
      heading: `Next ${NAME}`,
      datesBig: "Six Fridays — 6 November to 11 December 2026",
      chips: ["6 Nov", "13 Nov", "20 Nov", "27 Nov", "4 Dec", "11 Dec"],
      time: "10:00–12:30",
      venue: "Schiedam — exact location follows by email",
      line: "Six weekly gatherings · 2½ hours each · €190",
      capacity: "Maximum 10 mothers + babies · In English",
      ages: "For mothers with babies from around 6 weeks to 7 months",
      closedNote: "Registration is for the complete six-week circle, rather than individual sessions.",
      cta: "Join the circle",
    },
    space: {
      title: "Space for you within the early months",
      p1: "So much attention naturally turns towards your baby after birth. Feeding, sleeping, appointments, recovery and learning what your baby needs can fill the days very quickly.",
      p2: `${NAME} makes some room for you within all of that.`,
      p3: "For six weeks, the same small group of mothers and babies meets together. We talk about birth, the body after birth, becoming a mother, relationships and sharing the care, and your changing relationship with your baby.",
      p4: "There is also gentle movement, breathing and rest, tea and time simply to be together.",
      pull: "Your baby is part of the circle.",
      pullBody: "You can feed, change, walk around, settle your baby or stop what you're doing whenever you need to.",
    },
    baby: {
      title: "What if my baby cries, needs feeding or won't settle?",
      lead: "Then you take care of your baby.",
      p1: "Babies don't need to be quiet for you to participate.",
      p2: "You are welcome to breastfeed or bottle-feed, change a nappy, walk around, lie down with your baby, step out for a moment or simply listen while you settle them.",
      closing: "You don't need to apologise for your baby being a baby here.",
    },
    testimonials: {
      heading: "What mothers say",
      items: [
        { text: "I looked forward to the circle every week. It was one of the few places where I could arrive with my baby exactly as we were and didn't feel I had to have everything together. I loved having space to talk about my own experience of becoming a mother, not just about the baby.", name: "Lisa F." },
        { text: "What I appreciated most was meeting the same mothers each week. By the end it felt very easy to be together — feeding, changing babies, talking, sometimes moving and sometimes just sitting with a cup of tea. I felt there was space for both me and my baby.", name: "Tessie M." },
      ],
    },
    why6: {
      title: "Why six weeks together?",
      lead: "Connection takes time.",
      p1: "Meeting with the same women over six weeks gives the group time to become familiar.",
      p2: "You get to know one another and one another's babies. Conversations can continue from one week to the next, and you don't have to begin again with introductions every time.",
      p3: "The circle is small and closed for those six weeks, giving the group time to settle and relationships to grow naturally.",
    },
    weeks: {
      heading: "What we'll make space for",
      items: [
        { n: "Week 1", title: "Where Am I Now?", body: "Arriving together and making space for the reality of these first months." },
        { n: "Week 2", title: "The Birth I Had", body: "Space for some of what remains with you from your experience of giving birth." },
        { n: "Week 3", title: "The Body I Am Living In", body: "The physical reality of early motherhood — recovery, tiredness, feeding, carrying, movement, rest and what your body needs now." },
        { n: "Week 4", title: "Becoming a Mother", body: "Making room for the changes in identity, everyday life and expectations that can come with becoming someone's mother." },
        { n: "Week 5", title: "Sharing the Care", body: "Relationships, support, communication and how care is shared as family life changes around a new baby." },
        { n: "Week 6", title: "My Baby and Me", body: "Getting to know this particular little person and what you're discovering about one another as your relationship grows." },
      ],
    },
    rhythm: {
      heading: "What does being there actually feel like?",
      title: "A gentle rhythm each week",
      p1: "Each gathering has a gentle rhythm.",
      p2: "We begin by arriving with tea and giving everyone — including the babies — time to settle.",
      p3: "There is space for conversation around the week's theme, gentle postnatal movement, breathing or rest, and time for feeding, changing babies and simply being together.",
      p4: "Some weeks may include journaling, a simple creative practice, nurturing touch or baby massage.",
      closing: "We don't rush through a programme. There is a structure underneath each gathering, but enough flexibility to respond to the mothers and babies who are actually in the room that day.",
    },
    suitability: {
      title: "Is this circle for me?",
      p1: "This circle is designed for mothers with babies from around 6 weeks to 7 months old.",
      p2: "You don't need to have attended any Women's Den pregnancy or birth workshops beforehand.",
      p3: "You can join whether you had a vaginal or caesarean birth, whether you're breastfeeding, bottle-feeding or combining feeding, and whether this is your first baby or not.",
      p4: "The circle is held in English.",
    },
    faq: {
      heading: "Questions",
      items: [
        { q: "What if I miss a week?", a: "Of course, life with a baby is unpredictable. If you need to miss a week, that's okay. Your place remains yours for the full six-week circle and you can simply return the following week." },
        { q: "Can I join if my baby is younger or older than the suggested age?", a: "The circle is designed for mothers with babies from around 6 weeks to 7 months. The age range is a guide rather than a strict cut-off, so if your baby is a little younger or older, get in touch and we can see whether the circle would be a good fit." },
        { q: "Can I breastfeed or bottle-feed during the circle?", a: "Yes. Feeding your baby is completely welcome during the gathering." },
        { q: "What if my baby cries or needs settling?", a: "Babies don't need to be quiet for you to participate. You can feed, change, walk around, settle your baby, step out for a moment or simply listen while you care for them." },
        { q: "What should I bring?", a: "Bring whatever you normally need for your baby — nappies, feeding supplies and a change of clothes. Wear something comfortable that you can move and rest in. Everything we use during the circle will be provided." },
        { q: "Do I need yoga experience?", a: "No. The movement is gentle and designed for the postnatal period; no previous yoga experience is needed." },
        { q: "Can I join if I didn't attend a Women's Den pregnancy workshop?", a: `Yes. The ${NAME} stands on its own.` },
      ],
    },
    irida: {
      heading: "Held by Irida",
      body: "I'm Irida Hysenbelli, founder of Women's Den. I work with women around pregnancy, birth and the early months of motherhood, bringing together birth education, yoga and embodied practice. Holding a small closed group over six weeks is something I value deeply: there is time for trust to grow, for conversations to continue from week to week, and for every mother to be met exactly where she is that day.",
      link: "More about Irida",
    },
    crossLink: {
      title: "Need something more private?",
      body: "If something you're experiencing needs more individual attention, you can also meet with me privately through One-to-One Support for Mothers — a 90-minute session that belongs entirely to you.",
      cta: "Explore one-to-one support",
    },
    final: {
      title: "Come as you are. Bring your baby.",
      heading: `Next ${NAME}`,
      dates: "Six Fridays — 6 November to 11 December 2026",
      line1: "10:00–12:30 · Schiedam",
      line2: "Six weeks · 2½ hours weekly · €190 · Maximum 10 mothers + babies · In English",
      note: "Registration is for the complete six-week circle.",
      cta: "Join the circle",
      secondary: "Have a question? Get in touch",
    },
  },
  nl: {
    name: NAME,
    seo: {
      title: "Engelstalige moeder & baby groep | Postpartum | Rotterdam & Schiedam",
      description: "Een zesweekse postpartumkring voor moeders en baby's in Schiedam, vlakbij Rotterdam — de kring zelf wordt in het Engels gehouden. Deel je ervaring, ontmoet andere moeders en vind steun in de eerste maanden van het moederschap.",
    },
    hero: {
      eyebrow: "Er is een plek waar je met je baby kunt aankomen — en waar ook ruimte is voor jou.",
      sub: "Zes weken ruimte voor jou en je baby in de eerste maanden van het moederschap.",
      intro: "Een kleine wekelijkse kring waar moeders met hun baby's samenkomen om te praten, te bewegen, te rusten en te delen hoe zij deze eerste maanden ervaren.",
      facts: ["6 weken", "2½ uur per week", "€190", "Moeders + baby's", "Kleine groep", "In het Engels", "Schiedam"],
      primaryCta: "Doe mee met de volgende kring",
      secondaryCta: "Bekijk de data",
    },
    next: {
      heading: `Volgende ${NAME}`,
      datesBig: "Zes vrijdagen — 6 november tot 11 december 2026",
      chips: ["6 nov", "13 nov", "20 nov", "27 nov", "4 dec", "11 dec"],
      time: "10.00–12.30",
      venue: "Schiedam — exacte locatie volgt per e-mail",
      line: "Zes wekelijkse bijeenkomsten · 2½ uur per keer · €190",
      capacity: "Maximaal 10 moeders + baby's · In het Engels",
      ages: "Voor moeders met baby's van ongeveer 6 weken tot 7 maanden",
      closedNote: "Je inschrijving geldt voor de volledige zes weken, niet voor losse bijeenkomsten.",
      cta: "Doe mee met de kring",
    },
    space: {
      title: "Ruimte voor jou in de eerste maanden",
      p1: "Na de geboorte gaat natuurlijk veel aandacht naar je baby. Voeden, slapen, afspraken, herstel en ontdekken wat je baby nodig heeft kunnen de dagen snel vullen.",
      p2: `De ${NAME} maakt daarnaast ook ruimte voor jou.`,
      p3: "Zes weken lang komt dezelfde kleine groep moeders en baby's samen. We praten over de geboorte, je lichaam na de bevalling, moeder worden, relaties, hoe je de zorg samen verdeelt en je veranderende relatie met je baby.",
      p4: "Er is ook ruimte voor zachte beweging, ademhaling en rust, voor thee en om gewoon samen te zijn.",
      pull: "Je baby is onderdeel van de kring.",
      pullBody: "Je kunt voeden, verschonen, rondlopen, je baby in slaap wiegen of gewoon stoppen met wat je doet wanneer dat nodig is.",
    },
    baby: {
      title: "Wat als mijn baby huilt, gevoed moet worden of niet wil slapen?",
      lead: "Dan zorg je voor je baby.",
      p1: "Baby's hoeven niet stil te zijn om erbij te kunnen zijn.",
      p2: "Je bent welkom om borst- of flesvoeding te geven, een luier te verschonen, rond te lopen, met je baby te gaan liggen, even naar buiten te gaan of gewoon te luisteren terwijl je je baby troost.",
      closing: "Je hoeft je hier niet te verontschuldigen omdat je baby gewoon een baby is.",
    },
    testimonials: {
      heading: "Wat moeders zeggen",
      items: [
        { text: "Ik keek elke week uit naar de kring. Het was een van de weinige plekken waar ik met mijn baby kon komen zoals we waren, zonder het gevoel dat ik alles op orde moest hebben. Ik vond het fijn dat er ruimte was om over mijn eigen ervaring van moeder worden te praten, en niet alleen over de baby.", name: "Lisa F." },
        { text: "Wat ik het meest waardeerde, was dat ik elke week dezelfde moeders ontmoette. Tegen het einde voelde het heel vanzelfsprekend om samen te zijn — voeden, baby's verschonen, praten, soms bewegen en soms gewoon zitten met een kop thee. Ik voelde dat er ruimte was voor zowel mij als mijn baby.", name: "Tessie M." },
      ],
    },
    why6: {
      title: "Waarom zes weken samen?",
      lead: "Verbinding heeft tijd nodig.",
      p1: "Zes weken lang met dezelfde vrouwen samenkomen geeft jullie de tijd om vertrouwd met elkaar te raken.",
      p2: "Jullie leren elkaar en elkaars baby's kennen. Gesprekken kunnen van week tot week doorgaan en je hoeft niet iedere keer opnieuw met een voorstelronde te beginnen.",
      p3: "De cirkel blijft gedurende die zes weken klein en gesloten, zodat er vertrouwen kan ontstaan en contacten op een natuurlijke manier kunnen groeien.",
    },
    weeks: {
      heading: "Waar we ruimte voor maken",
      items: [
        { n: "Week 1", title: "Waar ben ik nu?", body: "Samen aankomen en ruimte maken voor de realiteit van deze eerste maanden." },
        { n: "Week 2", title: "De bevalling die ik had", body: "Ruimte voor wat je van je bevallingservaring met je meedraagt." },
        { n: "Week 3", title: "Het lichaam waarin ik leef", body: "De fysieke realiteit van het vroege moederschap — herstel, vermoeidheid, voeden, dragen, beweging, rust en wat je lichaam nu nodig heeft." },
        { n: "Week 4", title: "Moeder worden", body: "Ruimte voor de veranderingen in identiteit, dagelijks leven en verwachtingen die het moeder worden met zich mee kan brengen." },
        { n: "Week 5", title: "De zorg delen", body: "Relaties, steun, communicatie en hoe je samen de zorg vormgeeft terwijl het gezinsleven verandert met de komst van een baby." },
        { n: "Week 6", title: "Mijn baby en ik", body: "Dit bijzondere kleine mensje leren kennen — en ontdekken wat jullie over elkaar leren terwijl jullie relatie groeit." },
      ],
    },
    rhythm: {
      heading: "Hoe voelt het om er te zijn?",
      title: "Een zacht ritme elke week",
      p1: "Elke bijeenkomst heeft een zacht ritme.",
      p2: "We beginnen rustig, met thee en tijd voor iedereen — ook de baby's — om aan te komen.",
      p3: "Er is ruimte voor gesprek rond het thema van de week, zachte postnatale beweging, ademhaling of rust, en tijd om te voeden, te verschonen en gewoon samen te zijn.",
      p4: "Sommige weken is er ruimte voor journaling, een eenvoudige creatieve oefening, verzorgende aanraking of babymassage.",
      closing: "We haasten ons niet door een programma. Elke bijeenkomst heeft een structuur, maar er is genoeg ruimte om mee te bewegen met wat de moeders en baby's die dag nodig hebben.",
    },
    suitability: {
      title: "Is deze kring voor mij?",
      p1: "Deze kring is bedoeld voor moeders met baby's van ongeveer 6 weken tot 7 maanden.",
      p2: "Je hoeft vooraf geen zwangerschaps- of bevalworkshop van Women's Den te hebben gevolgd.",
      p3: "Je kunt meedoen of je nu vaginaal of met een keizersnede bent bevallen, borstvoeding of flesvoeding geeft of een combinatie daarvan, en of dit je eerste baby is of niet.",
      p4: "De kring wordt in het Engels gehouden.",
    },
    faq: {
      heading: "Veelgestelde vragen",
      items: [
        { q: "Wat als ik een week mis?", a: "Het leven met een baby is onvoorspelbaar — dat is helemaal oké. Als je een week moet missen, is dat prima. Je plek blijft van jou voor de volledige zesweekse cirkel en je kunt de week erna gewoon terugkomen." },
        { q: "Kan ik meedoen als mijn baby jonger of ouder is dan de voorgestelde leeftijd?", a: "De cirkel is ontworpen voor moeders met baby's van ongeveer 6 weken tot 7 maanden. De leeftijdsrange is een richtlijn, geen strikte grens — is je baby iets jonger of ouder, neem dan contact op, dan kijken we samen of de cirkel goed past." },
        { q: "Kan ik tijdens de kring borstvoeding of de fles geven?", a: "Ja. Je baby voeden is helemaal welkom tijdens de bijeenkomst." },
        { q: "Wat als mijn baby huilt of getroost moet worden?", a: "Baby's hoeven niet stil te zijn om mee te kunnen doen. Je kunt voeden, verschonen, rondlopen, je baby in slaap wiegen, even naar buiten gaan of gewoon luisteren terwijl je voor ze zorgt." },
        { q: "Wat moet ik meenemen?", a: "Neem mee wat je normaal nodig hebt voor je baby — luiers, voedingsspullen en een reserve-setje kleding. Draag zelf iets comfortabels waarin je kunt bewegen en rusten. Alles wat we tijdens de cirkel gebruiken, is aanwezig." },
        { q: "Heb ik yoga-ervaring nodig?", a: "Nee. De beweging is zacht en ontworpen voor de postnatale periode; yoga-ervaring is niet nodig." },
        { q: "Kan ik meedoen als ik geen zwangerschapsworkshop van Women's Den heb gevolgd?", a: `Ja. De ${NAME} staat op zichzelf.` },
      ],
    },
    irida: {
      heading: "Begeleid door Irida",
      body: "Ik ben Irida Hysenbelli, oprichter van Women's Den. Ik werk met vrouwen rond zwangerschap, geboorte en de eerste maanden van het moederschap, waarbij ik geboortevoorbereiding, yoga en lichaamsgerichte oefeningen samenbreng. Ik werk graag zes weken lang met dezelfde kleine groep. Het geeft ons de tijd om elkaar te leren kennen, vertrouwen op te bouwen en gesprekken van week tot week mee te nemen. Zo ontstaat er ook ruimte voor iedere moeder en voor wat zij op dat moment nodig heeft.",
      link: "Meer over Irida",
    },
    crossLink: {
      title: "Behoefte aan iets persoonlijkers?",
      body: "Als iets wat je ervaart meer persoonlijke aandacht vraagt, kun je ook individueel met me afspreken via Eén-op-één Begeleiding voor Moeders — een sessie van 90 minuten met alle ruimte voor jou en wat er op dat moment speelt.",
      cta: "Verken één-op-één begeleiding",
    },
    final: {
      title: "Kom zoals je bent. Neem je baby mee.",
      heading: `Volgende ${NAME}`,
      dates: "Zes vrijdagen — 6 november tot 11 december 2026",
      line1: "10.00–12.30 · Schiedam",
      line2: "Zes weken · 2½ uur per week · €190 · Maximaal 10 moeders + baby's · In het Engels",
      note: "Je inschrijving geldt voor de volledige zes weken.",
      cta: "Doe mee met de kring",
      secondary: "Nog een vraag? Neem contact op",
    },
  },
};
